import { isCasesHrefVisible } from '@/lib/hr/cases-access';
/**
 * NAV_GRAPH (D5 — 2026-08-04, Cmd-K cutover).
 *
 * Single structural SSOT for operator destinations:
 *   - L1 sidebar reads `NAV_V2` directly (curated density)
 *   - Cmd-K unions `flattenNavV2` + settings IA + **graph deep links** + residual
 *     static NAV_ITEMS (true orphans only)
 *
 * Module entitlement keys stay on legacy package paths; graph nodes use
 * operator URLs (People-canonical after dual-impl 4a–4c).
 */

import {
  FOOTER_NAV_V2,
  NAV_V2,
  type FlatNavV2Item,
  type NavV2GroupId,
  flattenNavV2,
} from './nav-v2';
import { COMPLIANCE_IA_GROUPS } from '@statura/utils/compliance-ia';
import { isShiftSwapNavigationPathVisible } from './shift-swap-navigation';

export type NavGraphFrequency = 'daily' | 'weekly' | 'rare';

export type NavGraphNode = {
  href: string;
  label: string;
  /** Primary domain when the node comes from L1; footer/deep for non-L1. */
  groupId: NavV2GroupId | 'footer' | 'deep';
  /**
   * Optional Cmd-K scope domain for demoted leaves so `> workforce` includes
   * create-worker / screening / courses after density demotes.
   */
  scopeGroupId?: NavV2GroupId;
  frequency: NavGraphFrequency;
  keywords: readonly string[];
};

type DeepLinkDef = Omit<NavGraphNode, 'groupId' | 'frequency'> & {
  scopeGroupId?: NavV2GroupId;
};

/**
 * Every compliance route is represented in the graph. Non-L1 routes become
 * Cmd-K deep links; L1 entries merge their canonical terms into the sidebar
 * node. The same registry decides both its fold and its scope.
 */
const COMPLIANCE_IA_DEEP_LINKS: readonly DeepLinkDef[] = COMPLIANCE_IA_GROUPS.flatMap((group) =>
  group.routes.map((route) => ({
    href: route.href,
    label: route.label,
    keywords: route.keywords,
    scopeGroupId: group.id === 'governance-provider' ? 'governance' : 'quality',
  })),
);

/** Rare / deep destinations that must stay discoverable after L1 density demotes. */
export const NAV_GRAPH_DEEP_LINKS: readonly DeepLinkDef[] = [
  {
    href: '/people/workers?view=recruitment',
    label: 'Recruitment',
    keywords: ['candidate', 'offer', 'hiring'],
    scopeGroupId: 'workforce',
  },
  {
    href: '/people/development?view=flexible-work',
    label: 'Flexible working requests',
    keywords: ['flexible', 'hours', 'written response'],
    scopeGroupId: 'workforce',
  },
  {
    href: '/people/policies-conduct/cases?view=grievances',
    label: 'Grievances',
    keywords: ['concern', 'protected disclosure'],
    scopeGroupId: 'workforce',
  },
  {
    href: '/people/policies-conduct/cases?view=return-to-work',
    label: 'Return to work',
    keywords: ['injury', 'suitable duties'],
    scopeGroupId: 'workforce',
  },
  {
    href: '/people/policies-conduct/cases?view=approvals',
    label: 'Action justifications',
    keywords: ['approvals', 'adverse action'],
    scopeGroupId: 'workforce',
  },
  {
    href: '/compliance/governance/qcab',
    label: 'Quality Care Advisory Body',
    keywords: ['qcab', 'quality care advisory body', 'board response', 'consumer member'],
    scopeGroupId: 'governance',
  },
  {
    href: '/compliance/evidence-packs',
    label: 'Evidence packs',
    keywords: ['regulator', 'quality standards', 'audit', 'immutable evidence'],
    scopeGroupId: 'quality',
  },
  {
    href: '/payroll',
    label: 'Payroll workspace',
    keywords: ['pay run', 'award', 'timesheet', 'finance'],
    scopeGroupId: 'workforce',
  },
  {
    href: '/people/workers',
    label: 'Workers',
    keywords: ['staff', 'register', 'people list', 'team members'],
    scopeGroupId: 'workforce',
  },
  {
    href: '/people/onboarding',
    label: 'Worker onboarding',
    keywords: [
      'onboarding',
      'new hire',
      'journey',
      'adoption',
      'assigned to me',
      'my onboarding work',
    ],
    scopeGroupId: 'workforce',
  },
  {
    href: '/people/compliance',
    label: 'People compliance',
    keywords: ['screening', 'credentials', 'work rights', 'renewals'],
    scopeGroupId: 'workforce',
  },
  {
    href: '/compliance/workforce/import',
    label: 'Import workforce compliance records',
    keywords: ['csv', 'import', 'workers', 'screening records'],
    scopeGroupId: 'workforce',
  },
  {
    href: '/people/learning',
    label: 'Learning',
    keywords: ['lms', 'training', 'courses', 'competency'],
    scopeGroupId: 'workforce',
  },
  {
    href: '/people/policies-conduct',
    label: 'Policies and conduct',
    keywords: ['policy', 'code of conduct', 'acknowledgement'],
    scopeGroupId: 'workforce',
  },
  {
    href: '/settings/people',
    label: 'People settings',
    keywords: ['presentation', 'onboarding packs', 'workforce config'],
    scopeGroupId: 'workforce',
  },
  {
    href: '/contractors',
    label: 'Contractors',
    keywords: ['agency', 'contractor'],
    scopeGroupId: 'workforce',
  },
  {
    href: '/people/development',
    label: 'Development',
    keywords: ['performance', 'pip', 'probation'],
    scopeGroupId: 'workforce',
  },
  {
    href: '/people/development/performance',
    label: 'Performance',
    keywords: ['pip', 'probation', 'review'],
    scopeGroupId: 'workforce',
  },
  {
    href: '/people/compliance/obligations',
    label: 'Workforce obligations',
    keywords: ['fair work', 'award', 'obligation register'],
    scopeGroupId: 'workforce',
  },
  {
    href: '/people/development/supervision',
    label: 'Clinical supervision',
    keywords: ['rn', 'en', 'supervisee'],
    scopeGroupId: 'workforce',
  },
  {
    href: '/people/evidence',
    label: 'Worker evidence',
    keywords: ['evidence pack', 'signing'],
    scopeGroupId: 'workforce',
  },
  {
    href: '/people/workers/new',
    label: 'Add worker',
    keywords: ['staff', 'hire', 'new', 'add worker'],
    scopeGroupId: 'workforce',
  },
  {
    href: '/people/workers/bulk-invite',
    label: 'Bulk invite workers',
    keywords: ['csv', 'import', 'invite'],
    scopeGroupId: 'workforce',
  },
  {
    href: '/people/learning/courses',
    label: 'Training courses',
    keywords: ['lms', 'catalogue', 'assign'],
    scopeGroupId: 'workforce',
  },
  {
    href: '/people/learning/analytics',
    label: 'Learning analytics',
    keywords: ['heatmap', 'competency', 'training dashboard'],
    scopeGroupId: 'workforce',
  },
  {
    href: '/people/compliance/screening',
    label: 'Worker screening',
    keywords: ['npc', 'ndis', 'police check'],
    scopeGroupId: 'workforce',
  },
  {
    href: '/people/compliance/certifications',
    label: 'Worker certifications',
    keywords: ['first aid', 'cpr', 'credential'],
    scopeGroupId: 'workforce',
  },
  {
    href: '/people/offboarding',
    label: 'Offboarding',
    keywords: ['exit', 'termination', 'resignation'],
    scopeGroupId: 'workforce',
  },
  {
    href: '/people/development/casual-conversion',
    label: 'Casual conversion',
    keywords: ['employee choice', 'permanent', 'fair work'],
    scopeGroupId: 'workforce',
  },
  {
    href: '/settings/people/assignment-requirements',
    label: 'Assignment requirements',
    keywords: ['roster', 'certification', 'ahpra', 'qualification'],
    scopeGroupId: 'workforce',
  },
  ...COMPLIANCE_IA_DEEP_LINKS,
  {
    href: '/compliance/governance/skills-matrix',
    label: 'Skills matrix',
    keywords: ['capability'],
  },
  {
    href: '/compliance/governance/conflicts',
    label: 'Conflict of interest',
    keywords: ['coi'],
  },
  { href: '/telehealth', label: 'Telehealth', keywords: ['video', 'remote'] },
  { href: '/monitoring', label: 'Remote monitoring', keywords: ['iot', 'devices'] },
  { href: '/care-plans/bulk-replan', label: 'Bulk re-plan', keywords: ['care plan cohort'] },
  { href: '/assessments/library', label: 'Assessment library', keywords: ['instrument catalogue'] },
  { href: '/medications', label: 'Medications', keywords: ['drug chart', 'pharmacy'] },
  {
    href: '/medications/monitoring',
    label: 'Medicine follow-up',
    keywords: ['warfarin', 'INR', 'clinical follow-up'],
    scopeGroupId: 'care',
  },
  {
    href: '/settings/comfort-coordination',
    label: 'Set up comfort coordination',
    keywords: ['palliative', 'comfort', 'clinical cover', 'handover'],
    scopeGroupId: 'care',
  },
  {
    href: '/medications/monitoring/setup',
    label: 'Set up medicine follow-up',
    keywords: ['warfarin', 'INR', 'provider ownership', 'protocol', 'opt in'],
    scopeGroupId: 'care',
  },
  { href: '/finance/respite-billing', label: 'Respite billing', keywords: ['respite'] },
  // Residential billing folds out of the facility-lead L1 set; keep it reachable from Cmd-K.
  {
    href: '/finance/residential-billing',
    label: 'Residential billing',
    keywords: ['residential billing', 'resident charges', 'month close', 'accommodation'],
  },
  // Overnight provider-managed billing build (2026-08-18): reachable via the
  // /finance sub-page tab strip (sub-page-tabs-registry.ts), kept out of the
  // NAV_V2 L1 leaf list — Finance is already at the ≤10 visible-leaf ceiling
  // for MIXED-provider FINANCE/EXEC personas (nav-optimisation-phase1.test.ts).
  {
    href: '/finance/billing',
    label: 'Billing run',
    keywords: ['billing run', 'invoice run', 'push', 'period', 'check', 'reconcile'],
  },
  {
    href: '/finance/billing/readiness',
    label: 'Billing period readiness',
    keywords: ['readiness', 'blockers', 'unbilled'],
  },
  {
    href: '/finance/payers',
    label: 'Payers',
    keywords: ['payer', 'head provider', 'household', 'grouping'],
  },
  {
    href: '/finance/charges',
    label: 'Charges',
    keywords: ['charge', 'travel', 'consumable', 'cancellation fee'],
  },
  {
    href: '/finance/statements',
    label: 'Payer balances',
    keywords: ['statement', 'payer balance', 'credit note'],
  },
  { href: '/pricing/rate-cards', label: 'SAH rate cards', keywords: ['pricing', 'rates'] },
  { href: '/head-office', label: 'Head Office', keywords: ['corporate', 'group'] },
  // Home me-* demoted from L1 density (Phase 1) — keep discoverable.
  {
    href: '/me/onboarding',
    label: 'My onboarding',
    keywords: ['onboarding', 'new starter', 'documents', 'sign'],
    scopeGroupId: 'home',
  },
  { href: '/dashboards/huddle', label: "Today's huddle", keywords: ['handover', 'shift'] },
  {
    href: '/dashboards/home-care-ops',
    label: 'Support at Home operations',
    keywords: ['home care ops', 'visits', 'undelivered'],
    scopeGroupId: 'ops',
  },
  {
    href: '/dashboards/facility-ops',
    label: 'Residential care overview',
    keywords: ['facility ops', 'command', 'exceptions'],
    scopeGroupId: 'ops',
  },
  {
    href: '/me/shift-offers',
    label: 'My shift offers',
    keywords: ['offer', 'accept', 'decline'],
  },
  { href: '/me/shift-swaps', label: 'My swaps', keywords: ['swap', 'shift'] },
  { href: '/me/timesheets', label: 'My timesheets', keywords: ['hours', 'pay'] },
  { href: '/me/messages', label: 'Staff messages', keywords: ['chat', 'colleague'] },
  {
    href: '/me/pay-documents',
    label: 'My pay documents',
    keywords: ['payslip', 'document'],
  },
  { href: '/me/leave', label: 'My leave', keywords: ['annual', 'sick', 'absence'] },
  {
    href: '/me/casual-conversion',
    label: 'My casual conversion',
    keywords: ['employee choice', 'permanent'],
  },
  { href: '/me/hours', label: 'My hours', keywords: ['hours bank'] },
  { href: '/me/preferences', label: 'My preferences', keywords: ['settings', 'profile'] },
  {
    href: '/me/contractor',
    label: 'My contractor portal',
    keywords: ['agency', 'contractor'],
  },
  {
    href: '/dashboards/workforce-analytics',
    label: 'Workforce analytics',
    keywords: ['turnover', 'agency', 'overtime'],
  },
  {
    href: '/dashboards/region-roll-up',
    label: 'Region roll-up',
    keywords: ['region', 'facilities'],
  },
  { href: '/care-pathways', label: 'Palliative pathways', keywords: ['palliative', 'eol'] },
  {
    href: '/clinical/medication-incidents',
    label: 'Medication incidents',
    keywords: ['near miss', 's8 exception'],
  },
  {
    href: '/clinical/caseload',
    label: 'RN caseload',
    keywords: ['sah', 'registered nurse'],
  },
  {
    href: '/clinical/delegations',
    label: 'Clinical delegations',
    keywords: ['delegate', 'task'],
  },
  {
    href: '/clinical/pathways',
    label: 'Pathway enrolments',
    keywords: ['clinical pathway'],
  },
  {
    href: '/clinical/restorative',
    label: 'Restorative care',
    keywords: ['goals', 'sessions'],
  },
  {
    href: '/clinical/overdue-assessments',
    label: 'Overdue assessments',
    keywords: ['assessment due'],
  },
  {
    href: '/medications/discrepancies',
    label: 'Med discrepancies',
    keywords: ['s8', 'count'],
  },
  { href: '/programs', label: 'Programs', keywords: ['program catalogue'] },
  {
    href: '/waitlist',
    label: 'Waitlist',
    keywords: ['intake', 'queue', 'referral'],
    scopeGroupId: 'care',
  },
  {
    href: '/finance/accommodation',
    label: 'Accommodation finance',
    keywords: ['rad', 'dap', 'bond'],
  },
  {
    href: '/residential/care-minutes/an-acc',
    label: 'AN-ACC register',
    keywords: ['an-acc', 'classification'],
  },
  {
    href: '/residential/dap-accrual',
    label: 'DAP reconciliation',
    keywords: ['dap', 'daily accommodation'],
  },
  {
    href: '/compliance/prudential/deposits',
    label: 'RAD/DAP deposits',
    keywords: ['prudential', 'deposit'],
  },
  { href: '/pricing', label: 'Pricing', keywords: ['rate', 'agreement'] },
  {
    href: '/analytics/facilities',
    label: 'Facility comparison',
    keywords: ['benchmark facilities'],
  },
  {
    href: '/analytics/benchmarking',
    label: 'Benchmarking',
    keywords: ['peer', 'compare'],
  },
  // P1c — coordinator L1 folds (URL stays; Cmd-K / parent still find them).
  {
    href: '/care-plans',
    label: 'All care plans',
    keywords: ['care plan book', 'plans'],
    scopeGroupId: 'care',
  },
  {
    href: '/assessments',
    label: 'Assessments',
    keywords: ['assessment library', 'nsaf'],
    scopeGroupId: 'care',
  },
  {
    href: '/people',
    label: 'People',
    keywords: ['workforce hub', 'staff'],
    scopeGroupId: 'workforce',
  },
  {
    href: '/people/workers',
    label: 'Workers',
    keywords: ['staff register', 'employees'],
    scopeGroupId: 'workforce',
  },
  {
    href: '/people/compliance',
    label: 'People compliance',
    keywords: ['screening', 'credentials', 'renewals'],
    scopeGroupId: 'workforce',
  },
  {
    href: '/people/onboarding',
    label: 'Onboarding',
    keywords: ['new hire', 'adoption'],
    scopeGroupId: 'workforce',
  },
  {
    href: '/people/learning',
    label: 'Learning',
    keywords: ['lms', 'training', 'courses'],
    scopeGroupId: 'workforce',
  },
  {
    href: '/people/policies-conduct',
    label: 'Policies & Conduct',
    keywords: ['policy', 'code of conduct'],
    scopeGroupId: 'workforce',
  },
  {
    href: '/home-care/readiness',
    label: 'SAH Readiness',
    keywords: ['delivery blocked', 'activation'],
    scopeGroupId: 'ops',
  },
  {
    href: '/home-care/budgets',
    label: 'SAH Budgets',
    keywords: ['package budget', 'drawdown'],
    scopeGroupId: 'ops',
  },
  {
    href: '/home-care/funding-changes',
    label: 'Funding Changes',
    keywords: ['payer', 'effective dated'],
    scopeGroupId: 'ops',
  },
  {
    href: '/cm-tasks',
    label: 'CM time',
    keywords: [
      'care management',
      'cm minutes',
      'log time',
      'coordination',
      'cm time',
      'not ready to submit',
    ],
    scopeGroupId: 'care',
  },
  {
    href: '/me/roster',
    label: 'My roster',
    keywords: ['my shifts', 'upcoming'],
    scopeGroupId: 'home',
  },
  {
    href: '/me/work-readiness',
    label: 'My work readiness',
    keywords: ['my onboarding', 'my credentials'],
    scopeGroupId: 'home',
  },
  {
    href: '/dashboards/caseload',
    label: 'My Caseload',
    keywords: ['caseload', 'my clients'],
    scopeGroupId: 'home',
  },
  {
    href: '/compliance/calendar?view=agenda',
    label: 'Compliance calendar agenda',
    keywords: ['deadlines', 'agenda', 'due dates', 'calendar'],
    scopeGroupId: 'home',
  },
  {
    href: '/observations',
    label: 'Observations',
    keywords: ['vitals', 'obs'],
    scopeGroupId: 'care',
  },
  {
    href: '/wounds',
    label: 'Wounds',
    keywords: ['wound', 'dressing'],
    scopeGroupId: 'care',
  },
  {
    href: '/medications/mar',
    label: 'MAR',
    keywords: ['medication administration', 'mar'],
    scopeGroupId: 'care',
  },
  {
    href: '/clinical/alerts',
    label: 'Clinical alerts',
    keywords: ['threshold', 'breach'],
    scopeGroupId: 'care',
  },
  {
    href: '/clinical/escalations',
    label: 'Escalations',
    keywords: ['escalation board'],
    scopeGroupId: 'care',
  },
  {
    href: '/clinical/handovers',
    label: 'Handovers',
    keywords: ['isbar', 'handover'],
    scopeGroupId: 'care',
  },
  {
    href: '/workforce/availability',
    label: 'Team availability',
    keywords: ['unavailability', 'patterns', 'worker availability', 'availability'],
    scopeGroupId: 'ops',
  },
  {
    href: '/me/availability',
    label: 'My availability',
    keywords: ['my unavailability', 'my availability', 'availability'],
    scopeGroupId: 'ops',
  },
  {
    href: '/residential',
    label: 'Residential',
    keywords: ['beds', 'occupancy'],
    scopeGroupId: 'ops',
  },
  {
    href: '/residential/occupancy',
    label: 'Occupancy',
    keywords: ['beds', 'leave', 'transfers', 'occupancy moves'],
    scopeGroupId: 'ops',
  },
  {
    href: '/workflows/instances',
    label: 'Workflows',
    keywords: ['workflow instances'],
    scopeGroupId: 'ops',
  },
  {
    href: '/contractors',
    label: 'Contractors',
    keywords: ['agency', 'contractor'],
    scopeGroupId: 'workforce',
  },
  {
    href: '/activity',
    label: 'Activity',
    keywords: ['audit log', 'who did what'],
    scopeGroupId: 'insights',
  },
  // P2a — facility-lead L1 folds that were not already on the graph.
  {
    href: '/home-care/service-delivery',
    label: 'Service Delivery',
    keywords: ['sah', 'visits', 'missed'],
    scopeGroupId: 'ops',
  },
  {
    href: '/analytics',
    label: 'Analytics Home',
    keywords: ['insights', 'analytics'],
    scopeGroupId: 'insights',
  },
  {
    href: '/reports',
    label: 'Reports',
    keywords: ['report catalogue', 'insights'],
    scopeGroupId: 'insights',
  },
  {
    href: '/finance/budgets/carry-over',
    label: 'Budgets',
    keywords: ['carry over', 'budget'],
    scopeGroupId: 'finance',
  },
  {
    href: '/settings/people',
    label: 'People settings',
    keywords: ['workforce settings', 'people config'],
    scopeGroupId: 'workforce',
  },
  {
    href: '/reports/subscriptions',
    label: 'Scheduled Reports',
    keywords: ['scheduled report', 'subscription'],
    scopeGroupId: 'insights',
  },
  {
    // /payroll became the operator fortnight command surface with #2229;
    // the queue lives one click inside it (2026-08-27).
    href: '/payroll',
    label: 'Payroll',
    keywords: ['pay-run queue', 'payroll', 'fortnight'],
    scopeGroupId: 'finance',
  },
  // P5 — folded roster aliases stay at URL and 307 to the live door.
  // Keywords merge onto the L1 Roster node so Cmd-K does not grow a second door.
  {
    href: '/roster/dispatch',
    label: 'Roster',
    keywords: [
      'board',
      'roster board',
      'grid',
      'roster grid',
      "today's roster",
      'today roster',
      'live shifts',
      'live roster',
      'dispatch',
    ],
    scopeGroupId: 'ops',
  },
  {
    href: '/roster/time-review',
    label: 'Time evidence',
    keywords: ['confirm the day', 'confirm day', 'time review', 'daily time review'],
    scopeGroupId: 'ops',
  },
  {
    href: '/roster/timesheets',
    label: 'Timesheets',
    keywords: ['approve timesheets', 'approve hours'],
    scopeGroupId: 'ops',
  },
  {
    href: '/workforce/timesheets/disputes',
    label: 'Worker queries',
    keywords: ['timesheet disputes', 'disputes'],
    scopeGroupId: 'ops',
  },
  {
    href: '/roster/timesheets/reconciliation',
    label: 'Pay period checks',
    keywords: ['period close ledger', 'reconciliation'],
    scopeGroupId: 'ops',
  },
] as const;

function frequencyForGroup(groupId: NavV2GroupId | 'footer'): NavGraphFrequency {
  if (groupId === 'home' || groupId === 'care' || groupId === 'ops' || groupId === 'workforce') {
    return 'daily';
  }
  if (groupId === 'quality' || groupId === 'finance') return 'weekly';
  return 'weekly';
}

function keywordsFromLabel(label: string): string[] {
  return label
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length > 2);
}

function mergeKeywordLists(
  current: readonly string[],
  extra: readonly string[],
): readonly string[] {
  if (extra.length === 0) return current;
  const seen = new Set(current.map((keyword) => keyword.toLowerCase()));
  const merged = [...current];
  for (const keyword of extra) {
    const normalised = keyword.trim().toLowerCase();
    if (!normalised || seen.has(normalised)) continue;
    seen.add(normalised);
    merged.push(keyword);
  }
  return merged.length === current.length ? current : merged;
}

function fromFlat(item: FlatNavV2Item): NavGraphNode {
  return {
    href: item.href,
    label: item.label,
    groupId: item.groupId,
    frequency: frequencyForGroup(item.groupId),
    keywords: keywordsFromLabel(item.label),
  };
}

/**
 * Build the current graph: L1 NAV_V2 + footer + deep links (deduped by href).
 * Sidebar still reads NAV_V2 directly; consumers should prefer this for
 * discoverability inventories and future Cmd-K.
 */
export function buildNavGraph(): readonly NavGraphNode[] {
  const byHref = new Map<string, NavGraphNode>();

  for (const item of flattenNavV2()) {
    byHref.set(item.href, fromFlat(item));
  }
  for (const item of FOOTER_NAV_V2) {
    byHref.set(item.href, {
      href: item.href,
      label: item.label,
      groupId: 'footer',
      frequency: 'rare',
      keywords: keywordsFromLabel(item.label),
    });
  }
  for (const deep of NAV_GRAPH_DEEP_LINKS) {
    const existing = byHref.get(deep.href);
    if (existing) {
      const keywords = mergeKeywordLists(existing.keywords, deep.keywords);
      if (keywords !== existing.keywords) {
        byHref.set(deep.href, { ...existing, keywords });
      }
      continue;
    }
    byHref.set(deep.href, {
      href: deep.href,
      label: deep.label,
      groupId: 'deep',
      scopeGroupId: deep.scopeGroupId,
      frequency: 'rare',
      keywords: deep.keywords,
    });
  }

  return [...byHref.values()].sort((a, b) => a.href.localeCompare(b.href));
}

/** Domain ids in display order (includes Workforce after D1 Option B). */
export const NAV_GRAPH_DOMAIN_ORDER: readonly NavV2GroupId[] = NAV_V2.map((g) => g.id);

/** Keywords string for cmdk `value` matching, keyed by operator href. */
export function navGraphKeywordsByHref(
  options: { shiftSwapsEnabled?: boolean } = {},
): ReadonlyMap<string, string> {
  const map = new Map<string, string>();
  for (const node of buildNavGraph()) {
    if (!isShiftSwapNavigationPathVisible(node.href, options.shiftSwapsEnabled)) continue;
    if (node.keywords.length === 0) continue;
    map.set(node.href, node.keywords.join(' '));
  }
  return map;
}

/**
 * Deep-link nodes not present as L1 sidebar leaves (Cmd-K discoverability after
 * density demotes). Excludes footer (Settings/Help already in FOOTER_NAV_V2).
 */
export function listNavGraphDeepLinks(
  options: { shiftSwapsEnabled?: boolean; role?: string | null } = {},
): readonly NavGraphNode[] {
  return buildNavGraph().filter(
    (node) =>
      node.groupId === 'deep' &&
      (options.role === undefined || isCasesHrefVisible(node.href, options.role)) &&
      isShiftSwapNavigationPathVisible(node.href, options.shiftSwapsEnabled),
  );
}

/** Group label for Cmd-K sections from graph groupId. */
export function navGraphGroupLabel(groupId: NavGraphNode['groupId']): string {
  if (groupId === 'deep') return 'More';
  if (groupId === 'footer') return 'Settings';
  const group = NAV_V2.find((g) => g.id === groupId);
  return group?.title ?? 'Pages';
}
